import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { Popconfirm } from "antd";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import CustomModal from "@/components/CustomModal";
import AliasAddForm from "@/components/BookingsDetails/PersonalInformationDetails/AliasAddForm";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "@/components/InmateDetails/styles";

const toBoolean = (value) => {
  if (typeof value === "string") {
    return ["true", "1", "y", "yes"].includes(value.trim().toLowerCase());
  }
  return !!value;
};

const normalizeAlias = (alias = {}) => {
  const id = alias?.id || alias?.tempId || uuidv4();
  return {
    ...alias,
    id,
    questionedIdentity: toBoolean(alias?.questionedIdentity),
  };
};

const stripLocalFields = (alias = {}) => {
  const { id, tempId, ...rest } = alias;
  return {
    ...rest,
    questionedIdentity: toBoolean(rest?.questionedIdentity),
  };
};

const DetentionAliasesList = ({
  aliases = [],
  disabled = false,
  onChange = () => {},
}) => {
  const aliasFormRef = useRef();
  const selectedAliasIndexRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAlias, setSelectedAlias] = useState(null);
  const [selectedAliasIndex, setSelectedAliasIndex] = useState(null);
  const [aliasData, setAliasData] = useState(() => aliases.map(normalizeAlias));

  useEffect(() => {
    const normalized = aliases.map(normalizeAlias);
    setAliasData(normalized);
  }, [aliases]);

  const emitChange = (updated) => {
    const sanitized = updated.map(stripLocalFields);
    onChange(sanitized);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedAlias(null);
    setSelectedAliasIndex(null);
    selectedAliasIndexRef.current = null;
    aliasFormRef.current?.resetForm();
  };

  // Open modal for adding a new alias
  const handleAdd = () => {
    if (disabled) return;
    setSelectedAlias(null);
    setSelectedAliasIndex(null);
    selectedAliasIndexRef.current = null;
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = () => {
    if (selectedAliasIndex === null) return;
    setIsEditing(true);
    setShowModal(true);
    setSelectedAlias(aliasData[selectedAliasIndex]);
  };

  const handleDelete = () => {
    if (disabled || selectedAliasIndex === null) return;
    const filtered = aliasData.filter((_, index) => index !== selectedAliasIndex);
    setAliasData(filtered);
    emitChange(filtered);
    setSelectedAlias(null);
    setSelectedAliasIndex(null);
    selectedAliasIndexRef.current = null;
  };

  const handleRowClick = (_alias, index) => {
    if (typeof index === "undefined" || index === null) return;
    const currentAlias = aliasData[index] || null;
    setSelectedAlias(currentAlias);
    setSelectedAliasIndex(currentAlias ? index : null);
    selectedAliasIndexRef.current = currentAlias ? index : null;
  };

  const handleSave = () => {
    aliasFormRef.current
      ?.validateFields()
      .then((values) => {
        const payload = {
          ...values,
          questionedIdentity: toBoolean(values?.questionedIdentity),
        };
        if (payload.dob) {
          const dob = dayjs(payload.dob);
          payload.dob = dob.isValid() ? dob.format("MM/DD/YYYY") : null;
        }

        if (
          isEditing &&
          selectedAliasIndex !== null &&
          aliasData[selectedAliasIndex]
        ) {
          const updated = aliasData.map((alias, index) =>
            index === selectedAliasIndex
              ? normalizeAlias({ ...alias, ...payload })
              : alias
          );
          setAliasData(updated);
          emitChange(updated);
        } else {
          // Adding new alias
          const newAlias = normalizeAlias({
            id: uuidv4(),
            ...payload,
          });
          const updated = [...aliasData, newAlias];
          setAliasData(updated);
          emitChange(updated);
        }
        closeModal();
      })
      .catch(() => {});
  };

  const tableData = useMemo(
    () =>
      aliasData.map((alias) => ({
        ...alias,
        questionedIdentity: toBoolean(alias.questionedIdentity)
          ? "True"
          : "False",
      })),
    [aliasData]
  );

  return (
    <>
      <InmateDetailsTableContainer>
        <TableHeader>
          <h6>Detention Aliases</h6>
          <div className="right">
            <Button onClick={handleAdd} disabled={disabled}>
              Add Alias
            </Button>
            <Button
              onClick={handleEdit}
              style={{ marginLeft: 10 }}
              disabled={selectedAliasIndex === null}
            >
              Details
            </Button>
            <Popconfirm
              title="Delete Alias"
              description="Are you sure you want to delete this alias?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              disabled={disabled || selectedAliasIndex === null}
            >
              <Button
                style={{ marginLeft: 10 }}
                disabled={disabled || selectedAliasIndex === null}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </TableHeader>

        <Table
          headers={[
            { key: "lName", text: "LName" },
            { key: "fName", text: "FName" },
            { key: "mName", text: "MName" },
            { key: "dob", text: "Date of Birth" },
            { key: "ssn", text: "SSN" },
            { key: "suffix", text: "Suffix" },
            { key: "questionedIdentity", text: "Questioned Identity" },
          ]}
          data={tableData}
          onRowClick={handleRowClick}
          rowClassName={(record) =>
            selectedAlias && record.id === selectedAlias.id ? "row-selected" : ""
          }
        />
      </InmateDetailsTableContainer>

      <CustomModal
        header="Alias Information"
        show={showModal}
        onHide={closeModal}
        onSaveClick={handleSave}
        onCloseClick={closeModal}
        hideFooter={disabled}
        designPattern="ant"
        width="55%"
        bodyStyle={{ padding: 20 }}
        submitButtonDisabled={disabled}
      >
        {showModal && (
          <AliasAddForm
            ref={aliasFormRef}
            formData={selectedAlias}
            bookingsFieldsEnabled={!disabled}
          />
        )}
      </CustomModal>
    </>
  );
};

export default DetentionAliasesList;
